import { ServiceType as RSDKServiceType } from '@rover/rsdk/src/apiClient/latest';
import { searchFilterKeysMap } from '@rover/shared/js/search/utilities/searchFiltersMap';
import { SearchFilters } from '@rover/types';
import type { MemberProfileSearchFilters } from '@rover/types/src/MemberProfileSearchFilters';
import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';
import type { ServiceType } from '@rover/types/src/ServiceType';

import { setAvailability } from './setAvailability';
import { setDates, setDatesIfExists } from './setDates';
import { setDaycareType } from './setDaycareType';
import { setManualFlags } from './setManualFlags';
import { setPage } from './setPage';
import { setPetSizes } from './setPetSizes';
import { setPetType } from './setPetType';
import { setRawLocationTypes } from './setRawLocationTypes';
import { setSelectedDays } from './setSelectedDays';
import { setSpacesRequired } from './setSpacesRequired';
import { removeEmptyKeys } from './utils';
import { validateAndSetSearchBounds } from './validateAndSetSearchBounds';

/**
 * @description This function is used to create api ready query params on the URLSearchParams object
 * @param filters
 * @returns URLSearchParams object with snakeified keys
 */
const initilizeURLSearchParams = <T extends object>(filters: T): URLSearchParams => {
  const searchParams = new URLSearchParams();
  Object.keys(filters).forEach((filterKey) => {
    const value = filters[filterKey];
    const snakeKey = searchFilterKeysMap[filterKey];
    if (Array.isArray(value)) {
      value.forEach((arrayValue) => {
        searchParams.append(snakeKey, arrayValue);
      });
    } else {
      searchParams.set(snakeKey, value);
    }
  });
  return searchParams;
};

/**
 *
 * @param filters
 * @param language
 * @returns URLSearchParams object with all params snake cased.
 */
export const memberProfileFiltersToURLSearchParams = (
  filters: MemberProfileSearchFilters,
  language: string
): URLSearchParams => {
  const cleanedFilters = removeEmptyKeys<MemberProfileSearchFilters>(filters);
  const searchParams = initilizeURLSearchParams(cleanedFilters);
  setDatesIfExists(searchParams, cleanedFilters, language);
  setSelectedDays(searchParams, cleanedFilters.selectedDays, cleanedFilters.frequency);
  return searchParams;
};

/**
 *
 * @param filters
 * @param serviceType
 * @param isDirty
 * @param language
 * @returns URLSearchParams object with all valid params snake cased.
 */
const filtersToURLSearchParams = (
  filters: Partial<SearchFilters>,
  serviceType: ServiceType | RSDKServiceType | null | undefined,
  isDirty: boolean,
  language: string
): URLSearchParams => {
  const cleanedFilters = removeEmptyKeys<Partial<SearchFilters>>(filters);
  const searchParams = initilizeURLSearchParams(cleanedFilters);

  // URLSearchParams object is passed by reference in JS,
  // thus any mutations that are done to object inside each function persists outside.
  setManualFlags(searchParams);
  setPage(searchParams, isDirty, cleanedFilters.page);
  setDaycareType(searchParams, cleanedFilters.inSittersHome, cleanedFilters.atDaycareFacility);
  setPetSizes(searchParams, cleanedFilters);
  setSpacesRequired(searchParams, cleanedFilters.spacesRequired, cleanedFilters.serviceType);
  setAvailability(
    searchParams,
    cleanedFilters.morningAvailability,
    cleanedFilters.middayAvailability,
    cleanedFilters.eveningAvailability
  );
  setPetType(searchParams, cleanedFilters.petType);
  setRawLocationTypes(searchParams, cleanedFilters.rawLocationTypes);
  setDates(
    searchParams,
    cleanedFilters.startDate,
    cleanedFilters.endDate,
    language,
    cleanedFilters.frequency,
    cleanedFilters.serviceType,
    serviceType
  );

  if (cleanedFilters.frequency === ServiceFrequencyEnum.REPEAT) {
    searchParams.set(searchFilterKeysMap.isAcceptingNewRecurringClients, 'true');
  }

  setSelectedDays(searchParams, cleanedFilters.selectedDays, cleanedFilters.frequency);

  validateAndSetSearchBounds(searchParams, cleanedFilters);

  return searchParams;
};

export default filtersToURLSearchParams;
